import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { resolveTradeEnvFields } from '../utils/tradeEnvFields';
import { buildWeeklyReview } from '../utils/weeklyReviews';
import {
  canPersistWeeklyReviewOnThisBuild,
  ensureWeeklyReviewNotebookPage,
} from '../utils/weeklyReviewNotebook';

function notificationKey(uid, review) {
  return `weekly-review-notice:${uid}:${review.accountId}:${review.weekKey}`;
}

const WeeklyReviewAutomation = ({ currentUser, accounts = [], onSelectAccount, dataRefreshTrigger }) => {
  const navigate = useNavigate();
  const [readyReviews, setReadyReviews] = useState([]);
  const [reviewScheduleTick, setReviewScheduleTick] = useState(0);

  const eligibleAccounts = useMemo(() => {
    if (process.env.NODE_ENV === 'production') return accounts;
    return accounts.filter((account) => canPersistWeeklyReviewOnThisBuild(account));
  }, [accounts]);

  useEffect(() => {
    const scheduleNextFridayClose = () => {
      const now = new Date();
      const next = new Date(now);
      const daysUntilFriday = (5 - now.getDay() + 7) % 7;
      next.setDate(now.getDate() + daysUntilFriday);
      next.setHours(16, 0, 0, 0);
      if (next <= now) next.setDate(next.getDate() + 7);
      return next.getTime() - now.getTime();
    };

    const timer = window.setTimeout(() => {
      setReviewScheduleTick((current) => current + 1);
    }, scheduleNextFridayClose());
    return () => window.clearTimeout(timer);
  }, [reviewScheduleTick]);

  useEffect(() => {
    if (!currentUser?.uid || eligibleAccounts.length === 0) {
      setReadyReviews([]);
      return undefined;
    }

    let cancelled = false;
    const generateReviews = async () => {
      try {
        const preferencesRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const preferencesSnap = await getDoc(preferencesRef);
        const preferences = preferencesSnap.exists() ? preferencesSnap.data() : {};
        const fieldDefinitions = resolveTradeEnvFields(preferences);
        const beThreshold = Number(preferences.beThreshold) || 20;

        const results = await Promise.all(eligibleAccounts.map(async (account) => {
          const entriesRef = collection(db, 'users', currentUser.uid, 'accounts', account.id, 'entries');
          const entriesSnap = await getDocs(entriesRef);
          const entries = entriesSnap.docs.map((entryDoc) => ({ id: entryDoc.id, ...entryDoc.data() }));
          const review = buildWeeklyReview({ account, entries, fieldDefinitions, beThreshold });
          if (!review) return null;
          const saved = await ensureWeeklyReviewNotebookPage({ uid: currentUser.uid, account, review });
          return { account, review: saved.review || review };
        }));

        if (cancelled) return;
        const unseen = results.filter(Boolean).filter(({ review }) => (
          window.localStorage.getItem(notificationKey(currentUser.uid, review)) !== 'seen'
        ));
        setReadyReviews(unseen);
      } catch (error) {
        console.error('Weekly review automation failed:', error);
      }
    };

    generateReviews();
    return () => { cancelled = true; };
  }, [currentUser, eligibleAccounts, dataRefreshTrigger, reviewScheduleTick]);

  const markSeen = () => {
    readyReviews.forEach(({ review }) => {
      window.localStorage.setItem(notificationKey(currentUser.uid, review), 'seen');
    });
    setReadyReviews([]);
  };

  const openCalendar = () => {
    const first = readyReviews[0];
    if (first?.account) onSelectAccount?.(first.account);
    markSeen();
    const today = new Date();
    navigate(`/month/${today.getFullYear()}/${today.getMonth() + 1}`);
  };

  if (readyReviews.length === 0) return null;
  const single = readyReviews.length === 1 ? readyReviews[0] : null;

  return (
    <div className="fixed right-4 top-4 z-[115] w-[min(340px,calc(100vw-2rem))]">
      <div className="theme-panel theme-border rounded-2xl border p-3.5 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="theme-accent-soft flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl">
            <CheckCircleIcon className="theme-accent h-4 w-4" />
          </span>
          <button type="button" onClick={openCalendar} className="min-w-0 flex-1 text-left">
            <div className="theme-text text-sm font-bold">
              {single ? 'Your weekly review is ready' : `${readyReviews.length} weekly reviews are ready`}
            </div>
            <div className="theme-text-muted mt-0.5 text-xs">
              {single ? `${single.review.accountName} - ${single.review.weekLabel}` : 'Open Calendar to review them.'}
            </div>
          </button>
          <button type="button" onClick={markSeen} className="theme-text-muted theme-hover-surface rounded-lg p-1" aria-label="Dismiss weekly review notification">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeeklyReviewAutomation;
